/**
 * Tier Management — View and edit all tier definitions grouped by region.
 *
 * Shows an expandable list of tiers with pricing, locations, and metadata.
 * Supports editing tier details, pricing, locations, tags, and amenities.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import type { TierRecord, TierPricing } from '@/types';
import { getTiers, saveTier, deleteTier, getRegions } from '@/services/adminApi';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FAMILY_SIZES = [
  { key: 'single', label: 'Single' },
  { key: 'couple', label: 'Couple' },
  { key: 'youngAdult', label: 'Young Adult' },
  { key: 'plusKid1', label: '+Kid 1' },
  { key: 'plusKid2', label: '+Kid 2' },
  { key: 'plusKid3', label: '+Kid 3' },
] as const;

const PLAN_TYPES = ['basic', 'premier', 'boxing'] as const;

function emptyPricing(): TierPricing {
  return { single: 0, couple: 0, youngAdult: 0, plusKid1: 0, plusKid2: 0, plusKid3: 0 };
}

function emptyTier(region: string): TierRecord {
  return {
    tierId: '',
    region,
    name: '',
    displayName: '',
    rank: 99,
    locations: [],
    boxingLocations: [],
    pricing: { basic: emptyPricing(), premier: emptyPricing(), boxing: null },
    tags: [],
    amenities: [],
    isPresale: false,
    isClubOnly: false,
  };
}

function formatPrice(p: number | undefined | null): string {
  if (p == null || p === 0) return '—';
  return `$${p.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Edit Dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  open: boolean;
  tier: TierRecord;
  isNew: boolean;
  saving: boolean;
  onChange: (t: TierRecord) => void;
  onSave: (finalTier: TierRecord) => void;
  onCancel: () => void;
}

function EditTierDialog({ open, tier, isNew, saving, onChange, onSave, onCancel }: EditDialogProps) {
  // Store raw strings for comma-separated fields so typing commas/spaces isn't swallowed
  const [locationsText, setLocationsText] = useState('');
  const [boxingLocationsText, setBoxingLocationsText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [amenitiesText, setAmenitiesText] = useState('');

  // Sync raw text from tier when dialog opens
  useEffect(() => {
    if (open) {
      setLocationsText(tier.locations.join(', '));
      setBoxingLocationsText(tier.boxingLocations.join(', '));
      setTagsText(tier.tags.join(', '));
      setAmenitiesText(tier.amenities.join(', '));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parse raw text to arrays and pass the final tier directly to onSave
  const handleSave = () => {
    const parseList = (text: string) => text.split(',').map(s => s.trim()).filter(Boolean);
    const finalTier: TierRecord = {
      ...tier,
      locations: parseList(locationsText),
      boxingLocations: parseList(boxingLocationsText),
      tags: parseList(tagsText),
      amenities: parseList(amenitiesText),
    };
    onSave(finalTier);
  };

  const updateField = <K extends keyof TierRecord>(key: K, value: TierRecord[K]) => {
    onChange({ ...tier, [key]: value });
  };

  const updatePricing = (type: 'basic' | 'premier' | 'boxing', field: keyof TierPricing, value: number) => {
    const current = tier.pricing[type] || emptyPricing();
    onChange({
      ...tier,
      pricing: {
        ...tier.pricing,
        [type]: { ...current, [field]: value },
      },
    });
  };

  const toggleBoxing = (enabled: boolean) => {
    onChange({
      ...tier,
      pricing: {
        ...tier.pricing,
        boxing: enabled ? emptyPricing() : null,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'Add Tier' : `Edit Tier: ${tier.displayName}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* Basic info */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Tier ID"
              value={tier.tierId}
              onChange={(e) => updateField('tierId', e.target.value)}
              fullWidth
              size="small"
              disabled={!isNew}
              helperText="e.g., tier-1b"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Name"
              value={tier.name}
              onChange={(e) => updateField('name', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Display Name"
              value={tier.displayName}
              onChange={(e) => updateField('displayName', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Rank"
              type="number"
              value={tier.rank}
              onChange={(e) => updateField('rank', parseInt(e.target.value) || 0)}
              fullWidth
              size="small"
              helperText="1=highest"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Region"
              value={tier.region}
              onChange={(e) => updateField('region', e.target.value)}
              fullWidth
              size="small"
              disabled={!isNew}
            />
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              control={<Switch checked={tier.isPresale} onChange={(e) => updateField('isPresale', e.target.checked)} />}
              label="Presale"
            />
          </Grid>
          <Grid item xs={3}>
            <FormControlLabel
              control={<Switch checked={!!tier.isClubOnly} onChange={(e) => updateField('isClubOnly', e.target.checked)} />}
              label="Club Only"
            />
          </Grid>
        </Grid>

        {/* Locations */}
        <TextField
          label="Locations (comma-separated)"
          value={locationsText}
          onChange={(e) => setLocationsText(e.target.value)}
          fullWidth
          size="small"
          multiline
        />
        <TextField
          label="Boxing Locations (comma-separated)"
          value={boxingLocationsText}
          onChange={(e) => setBoxingLocationsText(e.target.value)}
          fullWidth
          size="small"
        />

        {/* Tags & Amenities */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Tags (comma-separated)"
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              fullWidth
              size="small"
              helperText="e.g., Most Popular, Best Value"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Amenities (comma-separated)"
              value={amenitiesText}
              onChange={(e) => setAmenitiesText(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        {/* Pricing */}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Pricing (Monthly ACH)</Typography>

        <FormControlLabel
          control={<Switch checked={tier.pricing.boxing != null} onChange={(e) => toggleBoxing(e.target.checked)} />}
          label="Boxing available"
        />

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Family Size</TableCell>
              {PLAN_TYPES.map(type => (
                tier.pricing[type] != null && (
                  <TableCell key={type} align="center" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {type}
                  </TableCell>
                )
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {FAMILY_SIZES.map(({ key, label }) => (
              <TableRow key={key}>
                <TableCell>{label}</TableCell>
                {PLAN_TYPES.map(type => (
                  tier.pricing[type] != null && (
                    <TableCell key={type} align="center">
                      <TextField
                        type="number"
                        value={(tier.pricing[type] as TierPricing)?.[key] ?? 0}
                        onChange={(e) => updatePricing(type, key, parseFloat(e.target.value) || 0)}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ step: 0.01, min: 0 }}
                      />
                    </TableCell>
                  )
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !tier.tierId || !tier.name}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TierList() {
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [regions, setRegions] = useState<string[]>([]);
  const [tiers, setTiers] = useState<TierRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTier, setEditTier] = useState<TierRecord>(emptyTier('DMV'));
  const [editIsNew, setEditIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTier, setDeletingTier] = useState<TierRecord | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [allTiers, regionList] = await Promise.all([
        getTiers(regionFilter || undefined),
        getRegions(),
      ]);
      setTiers(allTiers);
      if (regionList.length > 0) setRegions(regionList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  }, [regionFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Group tiers by region
  const tiersByRegion = tiers.reduce<Record<string, TierRecord[]>>((acc, tier) => {
    const r = tier.region || 'Unknown';
    if (!acc[r]) acc[r] = [];
    acc[r].push(tier);
    return acc;
  }, {});

  // Sort regions alphabetically, each group by rank
  const sortedRegions = Object.keys(tiersByRegion).sort();
  for (const r of sortedRegions) {
    tiersByRegion[r].sort((a, b) => a.rank - b.rank);
  }

  const handleAdd = () => {
    setEditTier(emptyTier(regionFilter || 'DMV'));
    setEditIsNew(true);
    setDialogOpen(true);
  };

  const handleEdit = (tier: TierRecord) => {
    setEditTier({ ...tier });
    setEditIsNew(false);
    setDialogOpen(true);
  };

  const handleSave = async (finalTier: TierRecord) => {
    setSaving(true);
    try {
      await saveTier(finalTier);
      setEditTier(finalTier);
      setDialogOpen(false);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTier) return;
    try {
      await deleteTier(deletingTier.region, deletingTier.tierId);
      setDeleteConfirmOpen(false);
      setDeletingTier(null);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tier');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tier Management</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Region</InputLabel>
            <Select
              value={regionFilter}
              label="Region"
              onChange={(e) => setRegionFilter(e.target.value)}
            >
              <MenuItem value="">All Regions</MenuItem>
              {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>
          <Chip label={`${tiers.length} tiers`} color="primary" variant="outlined" />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Tier
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tiers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No tiers found. Seed the database or add a tier.</Typography>
        </Paper>
      ) : (
        sortedRegions.map(region => (
          <Accordion key={region} defaultExpanded={sortedRegions.length <= 3 || region === 'DMV'}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {region}
              </Typography>
              <Chip label={`${tiersByRegion[region].length} tiers`} size="small" sx={{ ml: 2 }} />
            </AccordionSummary>
            <AccordionDetails>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tier ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Display Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Basic</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Premier</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Boxing</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Locations</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tags</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiersByRegion[region].map(tier => (
                    <TableRow
                      key={tier.tierId}
                      hover
                      sx={{
                        backgroundColor: tier.isPresale ? '#fff8e1' : undefined,
                      }}
                    >
                      <TableCell>{tier.rank}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {tier.tierId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {tier.displayName}
                        </Typography>
                        {tier.isPresale && <Chip label="Presale" size="small" color="warning" sx={{ ml: 0.5 }} />}
                        {tier.isClubOnly && <Chip label="Club Only" size="small" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>{formatPrice(tier.pricing.basic?.single)}</TableCell>
                      <TableCell>{formatPrice(tier.pricing.premier?.single)}</TableCell>
                      <TableCell>{formatPrice(tier.pricing.boxing?.single)}</TableCell>
                      <TableCell>
                        <Tooltip title={tier.locations.join(', ')} arrow>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption">{tier.locations.length} locations</Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {tier.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEdit(tier)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => { setDeletingTier(tier); setDeleteConfirmOpen(true); }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Edit/Add Dialog */}
      <EditTierDialog
        open={dialogOpen}
        tier={editTier}
        isNew={editIsNew}
        saving={saving}
        onChange={setEditTier}
        onSave={handleSave}
        onCancel={() => setDialogOpen(false)}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Tier</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingTier?.displayName}</strong> ({deletingTier?.tierId}) from {deletingTier?.region}?
            This will not remove associated upgrade or access rules.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
